import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useApolloClient, useMutation } from "@apollo/client";

import SocketContext from "../../core/socketNotifications/SocketContext";
import history from "../../history";
import { getBooking } from "../../actions/booking/getBooking";
import {
  GET_PENDING_WORKER_REQUESTS,
  RESPOND_TIME,
  RESPOND_MATERIALS,
  RESPOND_WORKERS,
  CONTEST,
  respondToWorkerRequest,
  contestWorkerRequest,
} from "./workerRequestActions";

// GLOBAL mid-job worker-request prompt for the CLIENT — parity with the mobile app's
// home_controller `workerRequest-<userId>` listener that rings + opens a modal OVER ANY SCREEN.
// Mounted once inside Layout (every user-facing route, inside the Redux + Socket providers).
//
// The api emits `workerRequest-<userId>` for ALL three request types (time extension / extra
// helpers / materials). On that event we fetch the pending requests and show them in a single
// modal with the SAME Accept / Decline / Contest actions used by /job-notifications (reused via
// workerRequestActions). Materials still also flow through the in-tracking AdditionalRequestForm;
// this global prompt is additive and never blocks the booking/tracking flow.
//
// Guardrails: SSR-safe (no socket/DOM work on the server), listener registered ONCE with cleanup,
// and only ONE modal at a time (a new event just refreshes the open modal — never stacks).
const WorkerRequestModal = () => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const client = useApolloClient();
  const userId = useSelector((state) => state?.userAccount?.data?.userId);

  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [busyId, setBusyId] = useState(0);

  // Avoid overlapping fetches stacking modals when several events arrive close together.
  const fetchingRef = useRef(false);

  const [respondTime] = useMutation(RESPOND_TIME);
  const [respondMaterials] = useMutation(RESPOND_MATERIALS);
  const [respondWorkers] = useMutation(RESPOND_WORKERS);
  const [contestReq] = useMutation(CONTEST);
  const mutators = { respondTime, respondMaterials, respondWorkers, contestReq };

  const fetchPending = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data } = await client.query({
        query: GET_PENDING_WORKER_REQUESTS,
        variables: {},
        fetchPolicy: "network-only",
      });
      // The ring is ONLY for accept/decline worker requests (time / materials / workers). The
      // 'finalBill' (remaining balance to settle) is NOT a request to accept — it has no Accept/Decline
      // handler, so it used to pop here after approving materials and sit stuck forever (no handler, no
      // expiry). It belongs on the Job Notifications page with a real "Settle now" action (and the
      // settlement cron auto-charges it). Exclude it from the ring.
      const results = (data?.getPendingWorkerRequests?.results || [])
        .filter(Boolean)
        .filter((r) => r.type !== "finalBill");
      setRequests(results);
      // Only one modal at a time: open when there is something to show, otherwise close.
      setOpen(results.length > 0);
    } catch (e) {
      /* non-blocking: never freeze the UI on a fetch error */
    } finally {
      fetchingRef.current = false;
    }
  };

  // If the client is currently on the tracking screen, keep its booking data consistent so the
  // in-tracking UI (OrderStatus / BillingDetails) reflects the new request immediately.
  const refreshTrackingIfNeeded = () => {
    if (typeof window === "undefined") return;
    const path = (history && history.location && history.location.pathname) || "";
    const match = path.match(/^\/tracking\/(\d+)/);
    if (match) {
      try { dispatch(getBooking(Number(match[1]))); } catch (e) { /* ignore */ }
    }
  };

  useEffect(() => {
    // SSR-safe + idempotent: register the global listener exactly once per (socket,userId).
    if (typeof window === "undefined") return undefined;
    if (!socket || !userId) return undefined;

    const onWorkerRequest = () => {
      refreshTrackingIfNeeded();
      fetchPending();
    };

    socket.on(`workerRequest-${userId}`, onWorkerRequest);
    return () => {
      socket.off(`workerRequest-${userId}`, onWorkerRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, userId]);

  const onRespond = async (r, accept) => {
    setBusyId(r.id);
    try {
      await respondToWorkerRequest({ r, accept, mutators });
      refreshTrackingIfNeeded();
      await fetchPending();
    } catch (e) { /* ignore */ }
    setBusyId(0);
  };

  const onContest = async (r) => {
    setBusyId(r.id);
    try {
      const done = await contestWorkerRequest({ r, mutators });
      if (done) {
        refreshTrackingIfNeeded();
        await fetchPending();
      }
    } catch (e) { /* ignore */ }
    setBusyId(0);
  };

  if (!open || !requests.length) return null;

  return (
    <Modal
      show={open}
      animation={false}
      onHide={() => setOpen(false)}
      dialogClassName="commonModalContainer"
    >
      <Modal.Header closeButton className="commonModalHeader">
        <Modal.Title>
          <div className="commonModalTitle"><span>Job Update Request</span></div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="commonModalBody">
        {requests.map((r) => (
          <div key={`${r.type}-${r.id}`} style={{ background: "#fff", borderRadius: 12, padding: 18, marginBottom: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{r.title}</strong>
              <strong style={{ color: "#d6002a" }}>{r.currency || ""} {Number(r.amount || 0).toFixed(2)}</strong>
            </div>
            <div style={{ color: "#777", marginTop: 6 }}>{r.detail}</div>
            <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <Button variant="outline-danger" disabled={busyId === r.id} onClick={() => onRespond(r, false)}>Decline</Button>
              <Button variant="danger" disabled={busyId === r.id} onClick={() => onRespond(r, true)}>Accept</Button>
              <Button variant="link" disabled={busyId === r.id} onClick={() => onContest(r)} style={{ color: "#777" }}>Contest / propose a fair price</Button>
            </div>
          </div>
        ))}
      </Modal.Body>
    </Modal>
  );
};

export default WorkerRequestModal;
